import { parseThemeColors } from '../gameTemplates';

export const fillInTheBlanksTemplate = (data) => {
  const { title, questions, themeColors, themeFonts, backgroundImage } = data;
  const colors = parseThemeColors(themeColors);
  
  return `const InteractiveElement = () => {
  const [currentScreen, setCurrentScreen] = useState('start');
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [startTime, setStartTime] = useState(null);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const traceIdRef = useRef(null);

  useEffect(() => {
    try {
      const questions = ${JSON.stringify(questions, null, 2)};
      
      const questionsWithShuffledOptions = questions.map(q => ({
        ...q,
        shuffledOptions: [...q.options].sort(() => Math.random() - 0.5)
      }));
      
      setQuestions(questionsWithShuffledOptions);
      setUserAnswers({});
    } catch (error) {
      console.error('Error generating questions:', error);
      setQuestions([]);
    }
  }, []);

  useEffect(() => {
    try {
      const answeredCount = Object.keys(userAnswers).length;
      setIsSubmitEnabled(answeredCount === questions.length && questions.length > 0);
    } catch (error) {
      console.error('Error checking answers:', error);
      setIsSubmitEnabled(false);
    }
  }, [userAnswers, questions.length]);

  const handleBegin = () => {
    try {
      traceIdRef.current = crypto.randomUUID();
      const startTimeStamp = Date.now();
      setStartTime(new Date());
      setCurrentScreen('playing');
      
      if (typeof trackEvent === 'function') {
        trackEvent('interactive_start', traceIdRef.current, {
          startTime: startTimeStamp,
          status: 'started'
        });
      }
      
      if (window.parent && window.parent.postMessage) {
        window.parent.postMessage({
          type: 'interactive_start',
          data: {
            traceId: traceIdRef.current,
            startTime: startTimeStamp,
            status: 'started',
            url: window.location.href
          }
        }, '*');
      }
    } catch (error) {
      console.error('Error starting interactive:', error);
      setCurrentScreen('playing');
    }
  };

  const handleAnswerSelect = (questionId, value) => {
    try {
      setUserAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    } catch (error) {
      console.error('Error selecting answer:', error);
    }
  };

  const handleSubmit = () => {
    try {
      if (!isSubmitEnabled) return;
      
      const endTime = Date.now();
      const timeTaken = startTime ? Math.floor((endTime - startTime.getTime()) / 1000) : 0;
      const totalCorrect = questions.filter(q => userAnswers[q.id] === q.correctAnswer).length;
      const scorePercentage = questions.length > 0 ? Math.round((totalCorrect / questions.length) * 100) : 0;
      
      const questionsData = questions.map(q => ({
        id: q.id,
        question: q.question,
        correctAnswer: q.correctAnswer,
        userAnswer: userAnswers[q.id] || '',
        isCorrect: userAnswers[q.id] === q.correctAnswer,
        explanation: q.explanation
      }));
      
      if (typeof trackEvent === 'function') {
        trackEvent('interactive_end', traceIdRef.current, {
          totalScore: questions.length,
          timeTaken: timeTaken,
          userScore: totalCorrect,
          endTime: endTime,
          status: 'completed'
        });
      }
      
      if (window.parent && window.parent.postMessage) {
        window.parent.postMessage({
          type: 'interactive_end',
          data: {
            traceId: traceIdRef.current,
            endTime: endTime,
            timeTaken: timeTaken,
            totalQuestions: questions.length,
            totalScore: questions.length,
            userScore: totalCorrect,
            status: 'completed',
            url: window.location.href,
            questions: questionsData,
            summary: {
              totalCorrect: totalCorrect,
              totalIncorrected: questions.length - totalCorrect,
              accuracy: scorePercentage
            }
          }
        }, '*');
      }
      
      setCurrentScreen('summary');
    } catch (error) {
      console.error('Error submitting answers:', error);
      setCurrentScreen('summary');
    }
  };

  const handleRestart = () => {
    try {
      setCurrentScreen('start');
      setUserAnswers({});
      setStartTime(null);
      setIsSubmitEnabled(false);
      traceIdRef.current = null;
      
      setQuestions(prevQuestions => 
        prevQuestions.map(q => ({
          ...q,
          shuffledOptions: [...q.options].sort(() => Math.random() - 0.5)
        }))
      );
    } catch (error) {
      console.error('Error restarting game:', error);
      setCurrentScreen('start');
    }
  };

  const formatTime = (totalSeconds) => {
    try {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return minutes + 'm ' + seconds + 's';
    } catch (error) {
      return '0m 0s';
    }
  };

  const renderQuestionWithDropdown = (question, questionId) => {
    try {
      const questionObj = questions.find(q => q.id === questionId);
      if (!questionObj) return question;
      
      return (
        <div className="space-y-3">
          <div className="text-lg leading-normal" dangerouslySetInnerHTML={{ 
            __html: DOMPurify.sanitize(question) 
          }}></div>
          <Select value={userAnswers[questionId] || ''} onValueChange={(value) => handleAnswerSelect(questionId, value)}>
            <SelectTrigger className="w-full min-w-[200px] bg-[${colors.secondary}20] flex justify-between items-center text-xs lg:text-base p-2 lg:p-4">
              <SelectValue placeholder="Select answer" />
            </SelectTrigger>
            <SelectContent className="bg-white rounded-md shadow-md overflow-hidden">
              {questionObj.shuffledOptions.map((option, idx) => (
                <SelectItem key={idx} value={option}>
                  <div className="relative flex items-center w-full text-xs lg:text-base">
                    <span className="pl-1 py-1 pr-1 w-full hover:bg-gray-100" dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(option) 
                    }}>
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    } catch (error) {
      console.error('Error rendering question:', error);
      return <span className="text-lg text-red-500">Error loading question</span>;
    }
  };

  const totalCorrect = questions.filter(q => userAnswers[q.id] === q.correctAnswer).length;
  const totalTime = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
  const scorePercentage = questions.length > 0 ? Math.round((totalCorrect / questions.length) * 100) : 0;

  return (
    <div className="relative min-h-screen bg-cover bg-center bg-fixed" 
         style={{
        backgroundImage: 'url(${backgroundImage})',
        fontFamily: '${themeFonts}'
      }}>
      <div className="relative min-h-screen flex items-center justify-center p-4">

        {currentScreen === 'start' && (
          <div className="w-full max-w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="flex flex-col items-center justify-center gap-6 py-16 px-6">
              <h1 className="text-[${colors.primary}] text-[28px] font-bold leading-[125%] tracking-[-0.022rem] text-center">
                ${title}
              </h1>

              <p className="text-[#4E4E4E] text-[20px] font-medium leading-[125%] tracking-[-0.0165rem] text-center">
                Complete the sentences by filling in the blanks.
              </p>
              <button
                onClick={handleBegin}
                className="w-44 sm:w-[252px] h-10 sm:h-[48px] rounded-md bg-[${colors.primary}] hover:bg-[${colors.primary}90] focus:outline-none flex items-center justify-center transition-colors"
              >
                <span className="text-white text-[20px] font-semibold leading-[125%] tracking-[-0.0165rem]">
                  Begin
                </span>
              </button>
            </div>
          </div>
        )}

        {currentScreen === 'playing' && questions.length > 0 && (
          <div className="w-full max-w-[960px] bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-zinc-200 backdrop-blur-[20px] relative overflow-hidden">
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-gray-700 text-2xl font-bold mb-2">Fill in the Blanks</h2>
                <p className="text-neutral-600 text-lg">Select the correct word for each blank</p>
              </div>
              
              <div className="space-y-6 mb-8">
                {questions.map((question, idx) => (
                  <div key={question.id} className="flex gap-4 items-start">
                    <span className="text-gray-700 font-bold text-lg min-w-[30px]">{idx + 1}.</span>
                    <div className="flex-1">
                      {renderQuestionWithDropdown(question.question, question.id)}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center">
                <button 
                  onClick={handleSubmit} 
                  disabled={!isSubmitEnabled}
                  className="w-48 h-12 rounded-lg flex justify-center items-center transition-all"
                  style={{backgroundColor: isSubmitEnabled ? '#1E293B' : '#94A3B8'}}
                >
                  <span className="text-white text-xl font-semibold">Check Answers</span>
                </button>
              </div>
              
              <div className="mt-4 text-center text-neutral-600 text-sm">
                {Object.keys(userAnswers).length} of {questions.length} questions answered
              </div>
            </div>
          </div>
        )}

        {currentScreen === 'summary' && (
          <div className="w-full max-w-[807px] relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-[#E0DEEF] bg-[#FFFFFF] backdrop-blur-[20px] overflow-hidden">
            <div className="p-8">
              <h2 className="text-[${colors.primary}] text-[28px] font-bold tracking-[-0.022rem] text-center mb-4">
                Your Results
              </h2>
              
              <Alert className={\`mb-6 \${scorePercentage >= 70 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}\`}>
                <div className="flex items-center space-x-2">
                  <svg className={\`w-4 h-4 \${scorePercentage >= 70 ? 'text-green-600' : 'text-red-600'}\`} fill="currentColor" viewBox="0 0 20 20">
                    {scorePercentage >= 70 ? (
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    ) : (
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    )}
                  </svg>
                  <AlertTitle className={\`\${scorePercentage >= 70 ? 'text-green-800' : 'text-red-800'} font-semibold\`}>
                    {scorePercentage >= 70 ? 'Great Job' : 'Keep Practicing'}
                  </AlertTitle>
                </div>
                <AlertDescription className={\`\${scorePercentage >= 70 ? 'text-green-700' : 'text-red-700'} mt-2\`}>
                  {scorePercentage >= 70 
                    ? 'You demonstrated a good understanding of the concepts.' 
                    : 'Review the explanations below and try again to improve your score.'}
                </AlertDescription>
              </Alert>

              <hr className="my-4 w-1/2 mx-auto border-t-[#E0DEEF]" />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                  <div className="text-[#515151] text-xs mb-0.5 tracking-wide">Final Score</div>
                  <div className="text-[#2D2544] text-2xl font-bold">{scorePercentage}%</div>
                </div>
                <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                  <div className="text-[#515151] text-xs mb-0.5 tracking-wide">Total Time</div>
                  <div className="text-[#2D2544] text-2xl font-bold">{formatTime(totalTime)}</div>
                </div>
                <div className="bg-gradient-to-b from-[#FBFBFF] to-[#ECECF5] p-3 rounded-xl text-center shadow-sm">
                  <div className="text-[#515151] text-xs mb-0.5 tracking-wide">Correct</div>
                  <div className="text-[#2D2544] text-2xl font-bold">
                    {totalCorrect}/{questions.length}
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">S.No.</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Question</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Your Answer</th>
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-bold">Correct Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((question, idx) => {
                      const userAnswer = userAnswers[question.id];
                      const isCorrect = userAnswer === question.correctAnswer;
                      
                      return (
                        <tr key={question.id} className={isCorrect ? 'bg-green-50' : 'bg-red-50'}>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{idx + 1}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                            <span dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(question.question) 
                            }}></span>
                          </td>
                          <td className={\`border border-gray-300 px-3 py-2 text-sm font-semibold \${isCorrect ? 'text-green-700' : 'text-red-700'}\`}>
                            {userAnswer || 'Not answered'}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-green-700">
                            {question.correctAnswer}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mb-6">
                <h3 className="text-[${colors.primary}] text-xl font-bold mb-3">Explanations</h3>
                <div className="space-y-2">
                  {questions.map((question, idx) => (
                    <div key={question.id} className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm">
                        <span className="font-bold">Q{idx + 1}:</span> <span dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(question.explanation) 
                        }}></span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleRestart}
                  className="w-44 h-12 bg-[${colors.primary}] hover:bg-[${colors.primary}90] rounded-md text-white text-[20px] font-semibold transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}`;
};